import { find, isEmpty, isNil } from 'lodash';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { getBMI } from '@common/calculations';
import { Country, SelectOptions } from '@common/types';
import {
  HealthProposalRevisionData,
  ProposalConfig,
  ProposalFetchFn,
  ProposalSubmitFn
} from '@configs/adaptive-proposals/types';
import { AdaptiveProposalFormStep } from '@elements';
import insureAPIs from '@services/apis/insure';
import { HealthStatusBoundary } from '@store/health-status/types';
import { HealthAddOn, HealthPremium } from '@store/healthquote';
import { TermStatusBoundary } from '@store/term-status/types';
import { Premium, TermAddOn } from '@store/termquote';
import { dayjs } from '@ui';
import { deepCopyObject, isAgeChanged, scrollToTop } from '@utils';
import {
  COUNTRY_INDIA,
  DATE_FORMAT,
  HEALTH_INSURANCE_PRODUCTS
} from '@utils/constants';

import { HealthInsuredMemberBasic, ProductDetails, WithInsuredBasic } from './types';

type State = {
  // A step in the proposal.
  currentStep?: AdaptiveProposalFormStep;
  // All the steps in the proposal.
  steps: AdaptiveProposalFormStep[];
  // Indicates whether the Get proposal is in progress.
  loading: boolean;
  // The premium of the proposal.
  premium: HealthPremium | Premium;
  // basicProduct details
  productBasicDetails?: HealthStatusBoundary | TermStatusBoundary;
  // addons
  addOns: HealthAddOn[] | TermAddOn[];
  // The product details of the proposal.
  productDetails?: ProductDetails;
  //Product Name
  product?: string;
  // In health proposal, summary is WithInsuredBasic
  summary?: WithInsuredBasic | Record<string, any>;
  // Indicates whether the form is being submitted.
  submitting: boolean;
  // Indicates whether the previous step is from the review page.
  fromReview: boolean;
  // Any error that occurs while fetching the proposal.
  error?: string;
  // The last fetched proposal form.
  proposalForm: any;
  // Indicates whether the current proposal is a term insurance proposal.
  isTerm: boolean;
  premiumChangeGate: {
    // Show the PremiumChangeGate Alert
    showAlert: boolean;
    // Age change object.
    ageChanged?: ReturnType<typeof isAgeChanged>;
    // Indicates the medical practitioner flag of at least one member changed.
    doctorChanged?: boolean;
    // New premium after the change - to be shown in the alert.
    newPremium?: number;
    //impactedAddons flag
    addOnsImpacted?: Record<string, string>[];
    //new pincode
    newPincode?: string;
    //To show the modal with Invalid Application text
    invalidApplication?: boolean;
    //To indicate if the premiums have increased or decreased
    premiumsIncreased?: boolean;
  };
  //To show the BMI Modal
  bmiModal: {
    showAlert: boolean;
  };
  // Loading state when countries are being fetched
  loadingCountries: boolean;
  // Error state when fetching countries failed
  countriesError: '';
  // Countries Options List for Country Selection (Applicable in NRI journeys)
  countries: SelectOptions[];
  // Countries Options List for Country Selection (Applicable in NRI journeys)
  countriesSansIndia: SelectOptions[];
  // Countries from the boundaries API
  countryCallingCodes: Country[];
  // Async Function to fetch the proposal.
  onFetch: ProposalFetchFn;
  // Async Function to submit the proposal.
  onSubmit: ProposalSubmitFn;
  // Health Policy Revision Dialog state
  // If members are rejected or loading, the dialog is shown.
  healthPolicyRevision: {
    isShown: boolean;
    data: HealthProposalRevisionData | null;
  };
};

type Action = {
  /*
  Move to the next step in the form.
  */
  next: () => void;
  /*
  Move to the previous step in the form.
  */
  back: () => void;
  /*
  Go to the review step.
  */
  goToReview: () => void;
  /*
  Initialize the proposal store with the steps, onFetch and onSubmit functions.
  The URL is used to get the config for the proposal. Then the config is passed to this function.
  */
  initialize: (proposal: ProposalConfig, product: string) => void;
  /*
  Set the premium and product details.
  Premium and product details are set after fetching the proposal.
   */
  setPremiumAndProductDetails: ({
    premium,
    productDetails,
    product,
    addOns,
    productBasicDetails
  }: {
    premium: State['premium'];
    productDetails: State['productDetails'];
    product: State['product'];
    addOns: State['addOns'];
    productBasicDetails: State['productBasicDetails'];
  }) => void;
  /*
  Set the whole proposal form. This is used as the data in the Review page.
  */
  setSummary: (summary: State['summary']) => void;
  /*
  Indicates whether GET proposal request is in progress.
  */
  setLoading: (loading: State['loading']) => void;
  /*
  Indicates whether the form is being submitted.
  */
  setSubmitting: (submitting: State['submitting']) => void;
  /*
  Used to set the step to edit from the review page.
  */
  editStep: (stepIndex: AdaptiveProposalFormStep['step']) => void;
  /*
  Any error that occurs while fetching the proposal.
  */
  setError: (error: State['error']) => void;
  /*
  The last fetched proposal form. This is used to compare the current form with the fetched form.
  */
  setProposalForm: (proposalForm: State['proposalForm']) => void;
  /*
  Check if the pincode or age has changed. If yes, set the premiumChangeGate to show the alert.
  Otherwise submit the form.
  */
  onSubmitWithPremiumCheck: ProposalSubmitFn;
  /*
  Set attributes for the PremiumChangeGate component.
  */
  setPremiumChangeGate: (gate: Partial<State['premiumChangeGate']>) => void;
  /*
  Reset the PremiumChangeGate component.
  */
  resetPremiumChangeGate: () => void;
  /*Enable the BMI Modal */
  setBmiModal: (gate: Partial<State['bmiModal']>) => void;
  /*Reset the BMI Modal */
  resetBmiModal: () => void;
  fetchCountriesFromBoundaries: (product: string) => Promise<void>;
  // Dialog state for proposal submission
  setHealthPolicyRevision: (
    isShown: boolean,
    data?: HealthProposalRevisionData
  ) => void;
};

export const useProposalStore = create<State & Action>()(
  immer(
    devtools((set, get) => ({
      loading: false,
      submitting: false,
      currentStep: undefined,
      steps: [],
      premium: {
        tenure: 0,
        basePlan: 0,
        total: 0,
        tax: 0
      },
      addOns: [],
      productBasicDetails: undefined,
      productDetails: undefined,
      product: undefined,
      summary: undefined,
      fromReview: false,
      error: undefined,
      proposalForm: undefined,
      isTerm: false,
      premiumChangeGate: {
        showAlert: false
      },
      bmiModal: {
        showAlert: false
      },
      loadingCountries: false,
      countriesError: '',
      countries: [],
      countryCallingCodes: [COUNTRY_INDIA],
      countriesSansIndia: [],
      onFetch: async () => undefined,
      onSubmit: async () => ({ data: {}, success: false }),
      healthPolicyRevision: {
        isShown: false,
        data: null
      },

      next: () =>
        set(
          (state) => {
            if (!state.currentStep) return;
            if (state.currentStep.step < state.steps.length) {
              state.currentStep = state.steps[state.currentStep.step];
              state.fromReview = false;
            }
          },
          false,
          'next'
        ),
      back: () =>
        set(
          (state) => {
            if (!state.currentStep) return;

            if (state.currentStep?.step - 2 >= 0) {
              state.currentStep = state.steps[state.currentStep?.step - 2];
              state.fromReview = false;
            }
          },
          false,
          'back'
        ),
      goToReview: () =>
        set(
          (state) => {
            state.currentStep = state.steps[state.steps.length - 1];
          },
          false,
          'goToReview'
        ),
      initialize: ({ steps, onFetch: _onFetch, onSubmit }, product) => {
        const isTerm = window.location.pathname.includes('/term/');
        set(
          () => ({
            steps: steps,
            // change index to set default step (index+1) while testing
            currentStep: steps[0],
            onFetch: async (policyId: string) => {
              const supportsBoundaryCountries =
                isTerm ||
                product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE ||
                product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE_VYTL;

              if (
                get().countryCallingCodes.length === 1 &&
                supportsBoundaryCountries
              ) {
                await get().fetchCountriesFromBoundaries(product);
              }

              if (!isTerm) {
                return _onFetch(policyId, [], get().currentStep?.review);
              }

              return _onFetch(policyId, get().countryCallingCodes);
            },
            onSubmit,
            error: undefined,
            isTerm,
            premiumChangeGate: { showAlert: false },
            bmiModal: { showAlert: false }
          }),
          false,
          'initialize'
        );
      },
      setPremiumAndProductDetails: ({
        premium,
        productDetails,
        product,
        productBasicDetails,
        addOns
      }) => {
        const steps = get().steps;
        const filteredSteps = steps.filter((step) => {
          if (step.condition) {
            return step.condition(productDetails);
          }
          return true;
        });

        const transformedSteps = filteredSteps.map((step, index) => ({
          ...step,
          step: index + 1
        }));

        set(
          () => ({
            premium,
            productDetails,
            steps: transformedSteps,
            product,
            addOns,
            productBasicDetails
          }),
          false,
          'setPremiumAndProductDetails'
        );
      },
      setSummary: (summary) => set(() => ({ summary }), false, 'setSummary'),
      setLoading: (loading) => set(() => ({ loading }), false, 'setLoading'),
      setSubmitting: (submitting) =>
        set(() => ({ submitting }), false, 'setSubmitting'),
      editStep: (stepToEdit) =>
        set(
          (state) => ({
            currentStep: state.steps[stepToEdit] || state.steps[0],
            fromReview: true
          }),
          false,
          'editStep'
        ),
      setError: (error) => set(() => ({ error }), false, 'setError'),
      setProposalForm: (proposalForm) =>
        set(() => ({ proposalForm }), false, 'setProposalForm'),
      setHealthPolicyRevision: (
        isShown: boolean,
        data?: HealthProposalRevisionData
      ) => {
        set(
          () => ({
            healthPolicyRevision: {
              isShown,
              data: data || null
            }
          }),
          false,
          'setHealthPolicyRevision'
        );
      },
      onSubmitWithPremiumCheck: async (policyID, _values) => {
        const {
          premium,
          currentStep,
          proposalForm,
          fromReview,
          productDetails,
          goToReview,
          onSubmit,
          next,
          setPremiumChangeGate,
          setBmiModal
          // product
        } = get();

        const values = deepCopyObject(_values);

        // Normal submit function, does not expect any premium change
        const handleSubmit = async () => {
          try {
            const castedValues = currentStep?.schema?.cast(values);
            const response = await onSubmit(
              policyID!,
              castedValues,
              currentStep!.step,
              productDetails || []
            );
            if (
              response?.data?.data?.premium &&
              premium.total !== response?.data?.data?.premium
            ) {
              console.error('Premium changed');
            }
            if (fromReview) {
              goToReview();
            } else {
              next();
              scrollToTop();
            }
          } catch (error) {
            console.error(error);
          }
        };
        /*
          Pincode should be at proposer.address.pincode.

          If a pincode is already submitted, the submitted pincode is compared with the current pincode.
          Otherwise, the pincode from the quote is compared with the current pincode.
        */
        const handleSubmitWithPinCodeCheck = async () => {
          const pincodeInSubmittedForm = proposalForm?.proposer?.address?.pincode
            ? String(proposalForm?.proposer?.address?.pincode)
            : null;

          const currentPincode = values?.proposer?.address?.pincode
            ? String(values?.proposer?.address?.pincode)
            : null;

          const pincodeFromQuote = productDetails?.pincode
            ? String(productDetails?.pincode)
            : null;

          const pinCodeChanged =
            pincodeInSubmittedForm !== pincodeFromQuote ||
            pincodeInSubmittedForm !== currentPincode;

          // We need to call the comparePremiumChange API if pincode is changed
          if (pinCodeChanged) {
            const res = await insureAPIs.comparePremiumChange(policyID, {
              pincode: currentPincode!
            });

            const newPremium = res?.data?.revised_premium?.total;
            const oldPremium = res?.data?.current_premium?.total;

            if (res?.data) {
              if (res?.data?.is_application_invalid) {
                setPremiumChangeGate({
                  showAlert: true,
                  invalidApplication: true
                });
              } else {
                if (newPremium !== oldPremium) {
                  setPremiumChangeGate({
                    showAlert: true,
                    newPremium,
                    addOnsImpacted: res?.data?.impacted_addons,
                    newPincode: currentPincode || '',
                    premiumsIncreased: newPremium > oldPremium
                  });
                } else {
                  await handleSubmit();
                }
              }
            }
          } else {
            const res = await handleSubmit();
            return res;
          }
        };

        const isBMIOutOfRange = () => {
          const isAMinor = (dob: dayjs.Dayjs) => {
            // ERGO considers ages between 0 - 17Y, 11M 30D as a minor
            const minorThresholdDate = dayjs()
              .subtract(17, 'years')
              .subtract(11, 'months')
              .subtract(30, 'days');

            return !!dob?.isAfter(minorThresholdDate);
          };

          const { min: bmiMin, max: bmiMax } = currentStep?.bmiRange ?? {
            min: 10,
            max: 50
          };
          const members = values?.insured_basic || [];

          for (const member of members) {
            const { weight, height_ft, height_in, dob } = member;

            if (isAMinor(dayjs(dob!))) {
              continue; // Skip BMI check for minors
            }

            const bmi = getBMI(weight, height_ft, height_in);
            if (bmi < bmiMin || bmi > bmiMax) {
              return true;
            }
          }
          return false;
        };

        const handleSubmitWithBMICheck = async () => {
          if (isBMIOutOfRange()) {
            setBmiModal({
              showAlert: true
            });
            return { data: {}, success: false };
          } else {
            const res = await handleSubmit();
            return res;
          }
        };

        /*
          Expects dob of members in insured_basic[i].dob.
          If members are already submitted, the submitted members are compared with the current members.
          Otherwise, the members from the quote are compared with the current members
        */

        const handleSubmitWithPremiumChangeCheck = async () => {
          if (!productDetails?.members) {
            console.error('Members data not found');
            return;
          }

          const oldMemData = productDetails?.members;
          const newMemData = values?.insured_basic;
          const ageChanged = isAgeChanged(oldMemData, newMemData, true);

          /*
            The medical practitioner flag is only for reassure 3.0
          */
          const doctorChanged =
            !!currentStep?.checkPremiumIfDoctorChange &&
            newMemData?.some((member: HealthInsuredMemberBasic) => {
              const submittedMember = find(proposalForm?.insured_basic, {
                member_id: member.member_id
              });
              return (
                !isNil(submittedMember?.medical_pract) !== !!member.is_medical_pract
              );
            });

          if (ageChanged?.answer || doctorChanged) {
            // Edgecase: Validate BMI even before premium change gate as Continuing from Prem change submits without BMI check
            // This ensures BMI check is handled here and mix with PremiumChangeGate
            if (currentStep?.checkBMIChange && isBMIOutOfRange()) {
              setBmiModal({ showAlert: true });
              return { data: {}, success: false };
            }

            const filteredMembers = newMemData.map(
              (item: HealthInsuredMemberBasic) => ({
                dob: dayjs(item.dob).format(DATE_FORMAT),
                member_id: item.member_id,
                ...(currentStep?.checkPremiumIfDoctorChange && {
                  is_doctor: !!item.is_medical_pract
                })
              })
            );

            const res = await insureAPIs.comparePremiumChange(policyID, {
              members: filteredMembers
            });

            if (res?.data) {
              if (res?.data?.is_application_invalid) {
                setPremiumChangeGate({
                  showAlert: true,
                  invalidApplication: true
                });
                return res;
              } else {
                const newPremium = res?.data?.revised_premium?.total;
                const oldPremium = res?.data?.current_premium?.total;
                if (newPremium !== oldPremium) {
                  setPremiumChangeGate({
                    showAlert: true,
                    ageChanged,
                    doctorChanged,
                    newPremium,
                    premiumsIncreased: newPremium > oldPremium
                  });
                } else {
                  await handleSubmit();
                }
              }
            }
          } else {
            if (currentStep?.checkBMIChange) {
              return handleSubmitWithBMICheck();
            } else {
              const res = await handleSubmit();
              return res;
            }
          }
        };

        if (currentStep!.checkPremiumIfPinCodeChange) {
          return handleSubmitWithPinCodeCheck();
        } else if (
          currentStep!.checkPremiumIfAgeChange ||
          currentStep!.checkPremiumIfDoctorChange
        ) {
          return handleSubmitWithPremiumChangeCheck();
        }

        return handleSubmit();
      },
      // Fetch and store countries in store
      fetchCountriesFromBoundaries: async (product: string) => {
        const isHealthProduct =
          product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE ||
          product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE_VYTL;

        const getCountriesFromBoundaries = async () => {
          if (isHealthProduct) {
            const healthBoundariesResponse =
              await insureAPIs.getHealthProductBoundaries({
                products: [product]
              });
            return healthBoundariesResponse?.data?.[0]?.boundaries?.countries;
          }

          const productBoundariesResponse = await insureAPIs.getProductBoundaries({
            products: [product!],
            is_nri: false,
            gst_waiver: false
          });
          const countriesFromBoundaries =
            productBoundariesResponse?.data[0]?.boundaries?.countries;
          return countriesFromBoundaries;
        };

        set((state) => {
          state.loadingCountries = true;
          state.countriesError = '';
        });
        try {
          const countriesData = await getCountriesFromBoundaries();
          const countriesOptions =
            countriesData?.map((country) => ({
              label: country?.name,
              value: country?.name
            })) || [];

          const countriesOptionsSansIndia = countriesOptions.filter(
            (countryOption) => countryOption.value !== 'India'
          );

          let newCountries = countriesData;

          if (isEmpty(newCountries)) {
            newCountries = [COUNTRY_INDIA];
          }

          set(() => ({
            loadingCountries: false,
            countries: countriesOptions,
            countryCallingCodes: newCountries,
            countriesSansIndia: countriesOptionsSansIndia
          }));
        } catch (e) {
          // captureException(e);
          set(() => ({
            loadingCountries: false,
            countriesError: 'Something went wrong while fetching countries',
            countries: [],
            countriesSansIndia: []
          }));
        }
      },
      setPremiumChangeGate: (gate) =>
        set(
          (state) => ({
            premiumChangeGate: { ...state.premiumChangeGate, ...gate }
          }),
          false,
          'setPremiumChangeGate'
        ),
      resetPremiumChangeGate: () =>
        set(
          () => ({ premiumChangeGate: { showAlert: false } }),
          false,
          'resetPremiumChangeGate'
        ),
      setBmiModal: (gate) =>
        set(
          (state) => ({
            bmiModal: { ...state.bmiModal, ...gate }
          }),
          false,
          'setBmiModal'
        ),
      resetBmiModal: () =>
        set(() => ({ bmiModal: { showAlert: false } }), false, 'resetBmiModal')
    }))
  )
);
